import { supabase } from './supabase';
import type { OffsetCode, ApiResponse, FrequencyType } from '../types/database';

export interface CreateOffsetCodeData {
  company_id: string;
  code_number: string;
  name: string;
  frequency_type: FrequencyType;
}

/**
 * Service for managing offset codes
 */
class OffsetCodesService {
  /**
   * Get all offset codes for a company
   */
  async getOffsetCodes(companyId: string): Promise<ApiResponse<OffsetCode[]>> {
    try {
      const { data, error } = await supabase
        .from('offset_codes')
        .select('*')
        .eq('company_id', companyId)
        .order('code_number', { ascending: true });

      if (error) {
        console.error('Get offset codes Supabase error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      return {
        data: data as OffsetCode[],
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Get offset codes error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to fetch offset codes',
        },
        status: 500,
      };
    }
  }

  /**
   * Create a new offset code
   */
  async createOffsetCode(offsetCodeData: CreateOffsetCodeData): Promise<ApiResponse<OffsetCode>> {
    try {
      // Log the payload being sent
      console.log('Creating offset code with payload:', offsetCodeData);

      const { data, error } = await supabase
        .from('offset_codes')
        .insert({
          ...offsetCodeData,
          is_active: true,
        })
        .select()
        .single();

      if (error) {
        console.error('Create offset code Supabase error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      console.log('Offset code created successfully:', data);
      return {
        data: data as OffsetCode,
        error: null,
        status: 201,
      };
    } catch (error) {
      console.error('Create offset code error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to create offset code',
        },
        status: 500,
      };
    }
  }

  /**
   * Update an existing offset code
   */
  async updateOffsetCode(
    offsetCodeId: string,
    updates: Partial<CreateOffsetCodeData>
  ): Promise<ApiResponse<OffsetCode>> {
    try {
      const { data, error } = await supabase
        .from('offset_codes')
        .update(updates)
        .eq('id', offsetCodeId)
        .select()
        .single();

      if (error) {
        console.error('Update offset code Supabase error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      return {
        data: data as OffsetCode,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Update offset code error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to update offset code',
        },
        status: 500,
      };
    }
  }

  /**
   * Delete an offset code (soft delete by setting is_active to false)
   */
  async deleteOffsetCode(offsetCodeId: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('offset_codes')
        .update({ is_active: false })
        .eq('id', offsetCodeId);

      if (error) {
        console.error('Delete offset code Supabase error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      return {
        data: null,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Delete offset code error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to delete offset code',
        },
        status: 500,
      };
    }
  }

  /**
   * Toggle is_active status for an offset code
   */
  async toggleOffsetCodeActive(offsetCodeId: string, isActive: boolean): Promise<ApiResponse<OffsetCode>> {
    try {
      console.log('Toggling offset code active status:', { offsetCodeId, isActive });

      const { data, error } = await supabase
        .from('offset_codes')
        .update({ is_active: isActive })
        .eq('id', offsetCodeId)
        .select()
        .single();

      if (error) {
        console.error('Toggle offset code active Supabase error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      console.log('Offset code active status toggled successfully:', data);
      return {
        data: data as OffsetCode,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Toggle offset code active error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to toggle offset code active status',
        },
        status: 500,
      };
    }
  }
}

export const offsetCodesService = new OffsetCodesService();
